import type { GameState } from '../core/GameState';
import type { EventBus } from '../core/EventBus';

export type TutorialStep = 'welcome' | 'dispatch' | 'monitor' | 'correct' | 'validate' | 'tips' | 'done';

interface StepConfig {
  title: string;
  instruction: string;
  subtitle: string;
  target: number;
}

const STEPS: Record<TutorialStep, StepConfig> = {
  welcome: {
    title: 'WELCOME',
    instruction: 'You manage AI agents. They work for you — but they need supervision.',
    subtitle: 'Click anywhere to continue',
    target: 1,
  },
  dispatch: {
    title: '1. DISPATCH',
    instruction: '① Click a TASK card, then ② Click the AGENT to assign it',
    subtitle: 'Assign 2 tasks to continue',
    target: 2,
  },
  monitor: {
    title: '2. MONITOR',
    instruction: 'The agent DRIFTS while working (teal → red). Click it to CORRECT.',
    subtitle: 'Observe the drift for 5 seconds',
    target: 1,
  },
  correct: {
    title: '3. CORRECT',
    instruction: 'Click the drifting agent to recalibrate it before it fails!',
    subtitle: 'Correct the agent 2 times',
    target: 2,
  },
  validate: {
    title: '4. VALIDATE',
    instruction: 'Agent done → click it → compare outputs → APPROVE or REJECT',
    subtitle: 'Validate 2 outputs to continue',
    target: 2,
  },
  tips: {
    title: '5. TIPS',
    instruction: '[S] Spawn agents · [D] Despawn · [A] Aim Boost (+combo) · [M] Math (+life)',
    subtitle: 'Click anywhere to continue',
    target: 1,
  },
  done: {
    title: 'YOU\'RE READY!',
    instruction: '3 wrong answers = GAME OVER. Earn COMPUTE to spawn more agents. Good luck!',
    subtitle: '',
    target: 0,
  },
};

export class TutorialSystem {
  private state: GameState;
  private events: EventBus;
  step: TutorialStep = 'dispatch';
  progress = 0;
  active = false;
  private monitorTimer = 0;

  // UI elements
  private overlay: HTMLElement | null = null;
  private titleEl: HTMLElement | null = null;
  private instructionEl: HTMLElement | null = null;
  private subtitleEl: HTMLElement | null = null;
  private progressEl: HTMLElement | null = null;

  private onComplete: () => void;

  constructor(state: GameState, events: EventBus, onComplete: () => void) {
    this.state = state;
    this.events = events;
    this.onComplete = onComplete;

    this.createUI();
    this.wireEvents();
  }

  private createUI(): void {
    this.overlay = document.createElement('div');
    this.overlay.className = 'tutorial-overlay hidden';
    this.overlay.innerHTML = `
      <div class="tutorial-box">
        <div class="tutorial-step-title" id="tut-title"></div>
        <div class="tutorial-instruction" id="tut-instruction"></div>
        <div class="tutorial-subtitle" id="tut-subtitle"></div>
        <div class="tutorial-progress" id="tut-progress"></div>
      </div>
    `;
    document.getElementById('hud')?.appendChild(this.overlay);

    this.titleEl = document.getElementById('tut-title');
    this.instructionEl = document.getElementById('tut-instruction');
    this.subtitleEl = document.getElementById('tut-subtitle');
    this.progressEl = document.getElementById('tut-progress');
  }

  private stepEnteredAt = 0;

  private wireEvents(): void {
    // Welcome + Tips: any click advances (with 800ms cooldown to prevent double-skip)
    window.addEventListener('click', () => {
      if (!this.active) return;
      if (Date.now() - this.stepEnteredAt < 800) return;
      if (this.step === 'welcome' || this.step === 'tips') this.advanceStep();
    });

    this.events.on('task:assigned', () => {
      if (!this.active || this.step !== 'dispatch') return;
      this.progress++;
      this.updateUI();
      if (this.progress >= STEPS.dispatch.target) {
        this.advanceStep();
      }
    });

    this.events.on('agent:corrected', () => {
      if (!this.active || this.step !== 'correct') return;
      this.progress++;
      this.updateUI();
      if (this.progress >= STEPS.correct.target) {
        this.advanceStep();
      }
    });

    this.events.on('validation:decided', () => {
      if (!this.active || this.step !== 'validate') return;
      this.progress++;
      this.updateUI();
      if (this.progress >= STEPS.validate.target) {
        this.advanceStep();
      }
    });

  }

  start(): void {
    this.active = true;
    this.step = 'welcome';
    this.progress = 0;
    this.monitorTimer = 0;
    this.overlay?.classList.remove('hidden');
    this.updateUI();
  }

  stop(): void {
    this.active = false;
    this.overlay?.classList.add('hidden');
  }

  update(dt: number): void {
    if (!this.active) return;

    // Monitor step — just wait 5 seconds
    if (this.step === 'monitor') {
      this.monitorTimer += dt;
      const remaining = Math.max(0, 5 - this.monitorTimer);
      if (this.subtitleEl) {
        this.subtitleEl.textContent = remaining > 0
          ? `Observing... ${Math.ceil(remaining)}s`
          : 'Got it!';
      }
      if (this.monitorTimer >= 5) {
        this.advanceStep();
      }
    }
  }

  /** Should validations be auto-approved in this step? */
  shouldAutoApprove(): boolean {
    if (!this.active) return false;
    return this.step === 'welcome' || this.step === 'dispatch' || this.step === 'monitor' || this.step === 'correct' || this.step === 'tips';
  }

  /** Task speed multiplier (faster in tutorial) */
  getTaskSpeedMultiplier(): number {
    if (!this.active) return 1;
    if (this.step === 'dispatch') return 4; // 4x faster task completion
    return 1.5; // slightly faster in other steps
  }

  /** Get tutorial-specific difficulty overrides */
  getDifficultyOverrides(): Partial<{
    taskSpawnInterval: number;
    agentDriftSpeed: number;
    taskTimeout: number;
    correctionCooldown: number;
  }> | null {
    if (!this.active) return null;

    switch (this.step) {
      case 'welcome':
        return {
          taskSpawnInterval: 1.5, // spawn first task fast so it's ready for dispatch step
          agentDriftSpeed: 0,
          taskTimeout: 99,
          correctionCooldown: 0,
        };
      case 'dispatch':
        return {
          taskSpawnInterval: 3,
          agentDriftSpeed: 0,
          taskTimeout: 30,
          correctionCooldown: 0,
        };
      case 'monitor':
        return {
          taskSpawnInterval: 99,
          agentDriftSpeed: 0.08,
          taskTimeout: 99,
          correctionCooldown: 0,
        };
      case 'correct':
        return {
          taskSpawnInterval: 3,
          agentDriftSpeed: 0.12,
          taskTimeout: 30,
          correctionCooldown: 1,
        };
      case 'validate':
        return {
          taskSpawnInterval: 3,
          agentDriftSpeed: 0.02,
          taskTimeout: 30,
          correctionCooldown: 2,
        };
      case 'tips':
        return {
          taskSpawnInterval: 99,
          agentDriftSpeed: 0,
          taskTimeout: 99,
          correctionCooldown: 0,
        };
      default:
        return null;
    }
  }

  private advanceStep(): void {
    this.progress = 0;
    this.monitorTimer = 0;
    this.stepEnteredAt = Date.now();

    const steps: TutorialStep[] = ['welcome', 'dispatch', 'monitor', 'correct', 'validate', 'tips', 'done'];
    const currentIdx = steps.indexOf(this.step);
    this.step = steps[currentIdx + 1] || 'done';

    if (this.step === 'done') {
      this.updateUI();
      setTimeout(() => {
        this.stop();
        this.onComplete();
      }, 2500);
    } else {
      this.updateUI();
    }
  }

  private updateUI(): void {
    const config = STEPS[this.step];
    if (!config) return;

    if (this.titleEl) this.titleEl.textContent = config.title;
    if (this.instructionEl) this.instructionEl.textContent = config.instruction;
    if (this.subtitleEl) this.subtitleEl.textContent = config.subtitle;

    if (this.progressEl && config.target > 0 && this.step !== 'monitor' && this.step !== 'done') {
      this.progressEl.textContent = `${this.progress} / ${config.target}`;
    } else if (this.progressEl) {
      this.progressEl.textContent = '';
    }
  }

  dispose(): void {
    this.overlay?.remove();
  }
}
